import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Star, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import InquiryForm from '../components/InquiryForm';
import { tourPackages as mockTours } from '../mock';
import { destinationPagesData } from '../data/detailPages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TourPackages = () => {
  const [tours, setTours] = useState(mockTours);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/tour-packages`);
        if (res.data && res.data.length > 0) setTours(res.data);
      } catch (e) {}
    };
    fetch();
  }, []);

  const openInquiry = (tour) => {
    setSelected(tour);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        inquiry_type: 'tour',
        subject: `Tour Inquiry: ${selected?.name}`,
        details: { tour_name: selected?.name, destination: selected?.destination }
      });
      toast.success('Inquiry submitted! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setDialogOpen(false);
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="International Tour Packages from Surat - Kashmir, Bali, Dubai, Thailand, Vietnam"
        description="Best tour packages from Surat by Varni Visa. Kashmir, Sikkim, Bali, Dubai, Thailand, Vietnam holidays. Customizable itineraries with flights, hotels & sightseeing."
        keywords="International Tour Packages, Kashmir Tour Packages, Bali Tour Packages, Dubai Tour, Tour Packages from Surat"
        path="/tour-packages"
      />
      <section 
        className="relative h-[350px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(45, 141, 180, 0.85), rgba(247, 148, 93, 0.75)), url('https://images.pexels.com/photos/7079773/pexels-photo-7079773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Tour Packages</h1>
          <p className="text-xl">Discover the world with our curated tour packages</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tours.map((tour) => {
              // Try to find detail page by destination keyword
              const slug = Object.keys(destinationPagesData).find(
                (k) => tour.destination.toLowerCase().includes(k) || tour.name.toLowerCase().includes(k)
              );
              return (
                <Card key={tour.id} className="overflow-hidden hover:shadow-xl transition-all duration-300">
                  <div className="h-56 overflow-hidden relative">
                    <img
                      src={tour.image}
                      alt={tour.name}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    />
                    {tour.featured && (
                      <span className="absolute top-3 right-3 bg-[#F7945D] text-white text-xs px-3 py-1 rounded-full font-semibold">
                        Featured
                      </span>
                    )}
                  </div>
                  <CardHeader>
                    <CardTitle>{tour.name}</CardTitle>
                    <CardDescription className="flex items-center gap-1">
                      <MapPin size={14} />
                      {tour.destination}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{tour.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock size={14} />
                        {tour.duration}
                      </span>
                      <span className="text-2xl font-bold text-[#F7945D]">{tour.price}</span>
                    </div>
                  </CardContent>
                  <CardFooter className="gap-2">
                    {slug ? (
                      <Link to={`/destinations/${slug}`} className="flex-1">
                        <Button className="w-full bg-[#F7945D] hover:bg-[#e67f3d]">
                          View Itinerary <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        className="w-full bg-[#F7945D] hover:bg-[#e67f3d]"
                        onClick={() => openInquiry(tour)}
                      >
                        Book Now
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom inquiry */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-2">Looking for a Customized Tour?</h2>
          <p className="text-center text-gray-600 mb-8">Share your travel dream and we'll design your perfect itinerary</p>
          <InquiryForm inquiryType="tour" defaultSubject="Custom Tour Package Inquiry" />
        </div>
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book {selected?.name}</DialogTitle>
            <DialogDescription>
              Fill in your details and our travel experts will reach out
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="message">Travel Details</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Number of travelers, dates, special requirements..."
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-[#F7945D] hover:bg-[#e67f3d]" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Inquiry'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TourPackages;
